import * as React from 'react';
import { AreaAddSelect, AreaChip } from '@/components/ui/area-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  FilterChip,
  FilterChips,
  WorkspaceButton,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import type { AreaCoverage } from '@/lib/api/discovery';
import {
  EVIDENCE_CHANNELS,
  useLedgerQuery,
  useSetEvidenceAreas,
  useWithdrawEvidence,
  type EvidenceChannel,
  type EvidenceItem,
} from '@/lib/api/discovery-findings';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { cx } from '@/lib/utils/cn';
import { useDiscoveryNavigate } from '../discovery-layout';
import { formatDay, plural } from '../discovery-logic';
import { EvidenceCite, ShownOnScreenTag, SourceCell, WithdrawInline } from './evidence-bits';
import {
  CHANNEL_LABEL,
  EVIDENCE_STATUS_CHIP,
  coverageFlipText,
  ledgerStatusOptions,
  withdrawnToast,
  type LedgerStatusFilter,
} from './findings-logic';

/**
 * All evidence (E12-F06): every item in the engagement, numbered and
 * cited, with nothing deleted. Filter by status and method, search from
 * the list on the left, find what is not tagged to an area, change a
 * confirmed item's areas, and withdraw one with a typed reason; a
 * withdrawn or declined item stays listed, struck through. A row opens
 * its area (or, while awaiting review, its interview). Prototype:
 * `allEvidenceBody` in `docs/discovery/prototype/discovery.js`.
 */

const TH = 'h-[35px] border-b border-ink-100 px-3 text-left text-meta font-semibold text-ink-500';
const TD = 'border-b border-ink-100 px-3 py-2 align-middle';

export function EvidenceLedger({
  engagementId,
  areas,
  status,
  q,
  onStatusChange,
  onOpenArea,
}: {
  engagementId: string;
  areas: ReadonlyArray<AreaCoverage>;
  status: LedgerStatusFilter;
  q: string;
  onStatusChange: (status: LedgerStatusFilter) => void;
  onOpenArea: (areaId: string, evidenceId: string) => void;
}) {
  const [channel, setChannel] = React.useState<EvidenceChannel | 'all'>('all');
  const [untagged, setUntagged] = React.useState(false);
  const [withdrawing, setWithdrawing] = React.useState<string | null>(null);
  const [withdrawError, setWithdrawError] = React.useState<string | null>(null);
  // Search asks the server; wait for a pause in typing before asking.
  const [searched, setSearched] = React.useState(q);
  React.useEffect(() => {
    const t = setTimeout(() => setSearched(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const ledger = useLedgerQuery(engagementId, {
    status: status === 'all' ? undefined : status,
    channel: channel === 'all' ? undefined : channel,
    untagged,
    q: searched,
  });
  const withdraw = useWithdrawEvidence(engagementId);
  const setAreas = useSetEvidenceAreas(engagementId);
  const go = useDiscoveryNavigate(engagementId);
  const { toast } = useToast();

  const items = ledger.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = ledger.data?.pages[0]?.counts_by_status;
  const options = ledgerStatusOptions(counts);
  const total = options.find((o) => o.value === status)?.count ?? items.length;
  const areaLabel = new Map(areas.map((a) => [a.area_id, a.label]));

  const open = (e: EvidenceItem) => {
    const interview = e.citation.target['interview_id'];
    if (e.status === 'proposed' && typeof interview === 'string') {
      go({ to: 'sessions', interview });
      return;
    }
    if (e.area_ids[0]) onOpenArea(e.area_ids[0], e.id);
  };

  const changeAreas = (e: EvidenceItem, next: string[]) =>
    setAreas.mutate(
      { id: e.id, areaIds: next },
      {
        onSuccess: (r) => {
          const flip = coverageFlipText(r.coverage_changes);
          const names = r.area_ids.map((id) => areaLabel.get(id) ?? 'an area');
          toast({
            message: `${e.number ?? 'The item'} ${
              names.length
                ? `now counts toward ${names.join(', ')}.`
                : 'is no longer tagged to an area.'
            }${flip ? ` ${flip}` : ''}`,
          });
        },
        onError: (err) =>
          toast({
            message: err instanceof ApiError ? err.message : 'Could not change its areas.',
            tone: 'critical',
          }),
      },
    );

  const doWithdraw = (e: EvidenceItem, reason: string) => {
    setWithdrawError(null);
    withdraw.mutate(
      { id: e.id, reason, key: newIdempotencyKey() },
      {
        onSuccess: (r) => {
          setWithdrawing(null);
          toast({ message: withdrawnToast(e.number, r) });
        },
        onError: (err) =>
          setWithdrawError(err instanceof ApiError ? err.message : 'Could not withdraw it.'),
      },
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none flex-wrap items-center gap-2.5 border-b border-ink-100 px-4 py-2.5">
        <FilterChips
          aria-label="Filter by status"
          options={options}
          value={status}
          onChange={onStatusChange}
        />
        <span aria-hidden="true" className="mx-1 h-[18px] w-px bg-ink-100" />
        <select
          aria-label="Filter by method"
          value={channel}
          onChange={(e) => setChannel(e.target.value as EvidenceChannel | 'all')}
          className="h-7 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta text-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <option value="all">Any method</option>
          {EVIDENCE_CHANNELS.map((c) => (
            <option key={c} value={c}>
              {CHANNEL_LABEL[c]}
            </option>
          ))}
        </select>
        <FilterChip on={untagged} onClick={() => setUntagged((v) => !v)}>
          Not tagged to an area
        </FilterChip>
        <span className="flex-1" />
        <span className="whitespace-nowrap text-meta text-ink-500">
          {plural(total, 'item')}
          {q.trim() ? ` matching “${q.trim()}”` : ''} · click one to open its area
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" aria-busy={ledger.isFetching}>
        {ledger.error ? (
          <div className="flex flex-col items-center gap-3 p-8">
            <ErrorMessage error={ledger.error} />
            <WorkspaceButton onClick={() => void ledger.refetch()}>Try again</WorkspaceButton>
          </div>
        ) : !ledger.data ? (
          <div className="flex items-center justify-center gap-2 p-8 text-body-sm text-ink-500">
            <Spinner /> Loading the evidence…
          </div>
        ) : (
          <table className="w-full min-w-[720px] table-fixed border-separate border-spacing-0 text-body-sm">
            <colgroup>
              <col className="w-[72px]" />
              <col />
              <col className="w-[230px]" />
              <col className="w-[140px]" />
            </colgroup>
            <thead className="sticky top-0 z-[1] bg-paper-50">
              <tr>
                <th className={TH}>No.</th>
                <th className={TH}>Evidence and its areas</th>
                <th className={TH}>Source</th>
                <th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <LedgerRow
                  key={e.id}
                  e={e}
                  areas={areas}
                  areaLabel={areaLabel}
                  withdrawing={withdrawing === e.id}
                  busy={withdraw.isPending || setAreas.isPending}
                  withdrawError={withdrawing === e.id ? withdrawError : null}
                  onOpen={() => open(e)}
                  onStartWithdraw={() => {
                    setWithdrawError(null);
                    setWithdrawing(e.id);
                  }}
                  onCancelWithdraw={() => setWithdrawing(null)}
                  onWithdraw={(reason) => doWithdraw(e, reason)}
                  onChangeAreas={(next) => changeAreas(e, next)}
                />
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-14 text-center text-body-sm text-ink-500">
                    <strong className="mb-1 block text-h3 font-semibold text-ink-900">
                      Nothing matches
                    </strong>
                    Change the filters or the search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
        {ledger.hasNextPage ? (
          <div className="flex justify-center p-4">
            <WorkspaceButton
              disabled={ledger.isFetchingNextPage}
              onClick={() => void ledger.fetchNextPage()}
            >
              {ledger.isFetchingNextPage ? 'Loading…' : 'Show more'}
            </WorkspaceButton>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LedgerRow({
  e,
  areas,
  areaLabel,
  withdrawing,
  busy,
  withdrawError,
  onOpen,
  onStartWithdraw,
  onCancelWithdraw,
  onWithdraw,
  onChangeAreas,
}: {
  e: EvidenceItem;
  areas: ReadonlyArray<AreaCoverage>;
  areaLabel: ReadonlyMap<string, string>;
  withdrawing: boolean;
  busy: boolean;
  withdrawError: string | null;
  onOpen: () => void;
  onStartWithdraw: () => void;
  onCancelWithdraw: () => void;
  onWithdraw: (reason: string) => void;
  onChangeAreas: (next: string[]) => void;
}) {
  const gone = e.status === 'withdrawn' || e.status === 'declined' || e.status === 'superseded';
  const confirmed = e.status === 'confirmed';
  const opens = e.status === 'proposed' || e.area_ids.length > 0;
  const chip = EVIDENCE_STATUS_CHIP[e.status];
  const stop = (ev: React.SyntheticEvent) => ev.stopPropagation();
  const free = areas.filter((a) => !e.area_ids.includes(a.area_id));
  const note =
    e.status === 'withdrawn'
      ? `Withdrawn${e.withdrawn_at ? ` on ${formatDay(e.withdrawn_at)}` : ''}${e.withdraw_reason ? `: “${e.withdraw_reason.replace(/[.\s]+$/, '')}”` : ''}. It stays on record and no longer counts.`
      : e.status === 'declined'
        ? `Declined${e.decline_reason_text ? `: ${e.decline_reason_text}` : ''}. It never counts.`
        : e.hold_reason === 'person_unresolved'
          ? 'Held until the person is confirmed as new or the same as someone named.'
          : null;

  return (
    <tr
      onClick={opens ? onOpen : undefined}
      title={opens ? undefined : 'Not tagged to an area yet'}
      className={cx('group', opens && 'cursor-pointer hover:bg-paper-50')}
    >
      <td className={cx(TD, 'font-mono text-meta font-medium text-ink-700')}>
        {e.number && opens ? (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              onOpen();
            }}
            className="rounded-chip hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
          >
            {e.number}
          </button>
        ) : (
          (e.number ?? <span className="text-ink-300">—</span>)
        )}
      </td>
      <td className={TD}>
        <p className={cx('leading-5', gone ? 'text-ink-300 line-through' : 'text-ink-900')}>
          {e.text}
          {e.shown_on_screen ? (
            <>
              {' '}
              <ShownOnScreenTag iconOnly />
            </>
          ) : null}
        </p>
        {note ? <p className="mt-1 text-meta text-ink-500">{note}</p> : null}
        <div
          className="mt-1.5 flex flex-wrap items-center gap-1"
          onClick={confirmed ? stop : undefined}
        >
          {e.areas.map((a) => (
            <AreaChip
              key={a.area_id}
              title={a.active ? undefined : 'Retired area: it no longer counts'}
              onRemove={
                confirmed && !busy
                  ? () => onChangeAreas(e.area_ids.filter((id) => id !== a.area_id))
                  : undefined
              }
            >
              {a.label ?? areaLabel.get(a.area_id) ?? 'Unknown area'}
            </AreaChip>
          ))}
          {e.areas.length === 0 ? (
            <span className="text-meta text-ink-500">Not tagged to an area</span>
          ) : null}
          {confirmed && !busy ? (
            <AreaAddSelect
              options={free.map((a) => ({ id: a.area_id, label: a.label }))}
              onAdd={(id) => onChangeAreas([...e.area_ids, id])}
            />
          ) : null}
        </div>
        {withdrawing ? (
          <div onClick={stop}>
            <WithdrawInline
              number={e.number}
              busy={busy}
              error={withdrawError}
              onWithdraw={onWithdraw}
              onCancel={onCancelWithdraw}
            />
          </div>
        ) : null}
      </td>
      <td className={TD}>
        <SourceCell source={e.source} />
        <div className="mt-1 min-w-0">
          <EvidenceCite
            citation={e.citation}
            withdrawn={e.status === 'withdrawn'}
            reason={e.withdraw_reason}
          />
        </div>
      </td>
      <td className={TD} onClick={confirmed ? stop : undefined}>
        <div className="flex flex-col items-start gap-1">
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
          {confirmed && !withdrawing ? (
            <button type="button" className={linkishClass()} onClick={onStartWithdraw}>
              Withdraw
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
